import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  CountryISO,
  NgxIntlTelInputModule,
  PhoneNumberFormat,
  SearchCountryField,
} from 'ngx-intl-tel-input-gg';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { ChipModule } from 'primeng/chip';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { FileUploadHandlerEvent, FileUploadModule } from 'primeng/fileupload';
import { ImageModule } from 'primeng/image';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ToastModule } from 'primeng/toast';
import { Observable, catchError, concatMap, last, lastValueFrom, of, tap } from 'rxjs';
import { SpecialistTableComponent } from 'src/app/components/tables/specialist/specialist-table.component';
import { DateMinutes } from 'src/app/models/date-duration.model';
import { Day } from 'src/app/models/day.model';
import { PhoneNumber } from 'src/app/models/phone.model';
import { Specialist, WeekDays } from 'src/app/models/specialist.model';
import { SideBarMenuService } from 'src/app/services/sidebar-menu.service';
import { SpecialistService } from 'src/app/services/specialist.service';
import {
  DATE_MINUTES,
  WEEK_DAYS,
  generatePreview,
  validateImage,
} from 'src/app/services/utils.service';
import { dateValidator } from 'src/app/validators/date.validator';
import { endAtAfterStartAtValidator } from 'src/app/validators/endAt-after-startAt.validator';

@Component({
  selector: 'specialists',
  standalone: true,
  imports: [
    BreadcrumbModule,
    AsyncPipe,
    SpecialistTableComponent,
    ReactiveFormsModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    ToastModule,
    InputTextareaModule,
    FileUploadModule,
    ImageModule,
    DropdownModule,
    CalendarModule,
    ChipModule,
    CommonModule,
    NgxIntlTelInputModule,
  ],
  providers: [MessageService],
  templateUrl: './specialists.component.html',
})
export class SpecialistsComponent implements OnInit {
  private specialistService = inject(SpecialistService);
  private sidebarMenuService = inject(SideBarMenuService);
  private afs = inject(AngularFirestore);
  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);
  private storage = inject(AngularFireStorage);

  specialists$: Observable<Specialist[]>;
  specialistId: string;

  days: Day[] | undefined;
  dateMinutes: DateMinutes[] | undefined;

  appName: string = localStorage.getItem('appName');

  home: MenuItem;
  breadcrumbItems: MenuItem[] = [];
  viewModal: boolean = false;
  viewModalNested: boolean = false;
  loading: boolean = false;

  selectedFile: File | null;
  previewUrl: string | ArrayBuffer | null = null;

  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  onlyCountries: CountryISO[] = [CountryISO.Spain];

  form = this.fb.group({
    name: ['', [Validators.required]],
    curriculum: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    phoneNumber: [null as PhoneNumber | null, [Validators.required]],
    specialty: ['', [Validators.required]],
    dateDuration: [null as DateMinutes | null, [Validators.required]],
    datePrice: this.appName !== 'Caser MIMAvet' ? ['', [Validators.required]] : [''],
    fileImage: new FormControl(null, [validateImage]),
  });

  formNested = this.fb.group(
    {
      day: [null as Day | null, [Validators.required]],
      startAt: [null as Date | null, [Validators.required, dateValidator()]],
      endAt: [null as Date | null, [Validators.required, dateValidator()]],
    },
    { validators: endAtAfterStartAtValidator('startAt', 'endAt') },
  );

  diary: { [key: string]: { start: string; end: string }[] } = {};

  private isInitialTimeSetStartAt = false;
  private isInitialTimeSetEndAt = false;

  ngOnInit(): void {
    this.home = {
      icon: 'pi pi-fw pi-mobile',
      routerLink: '/',
      command: () => this.sidebarMenuService.clearSessionStorage(),
    };
    this.breadcrumbItems = [{ label: 'Especialistas', styleClass: 'font-bold' }];
    this.specialists$ = this.specialistService.findSpecialists();
    this.specialistId = this.afs.createId();
    this.days = WEEK_DAYS;
    this.dateMinutes = DATE_MINUTES;
  }

  add() {
    this.viewModal = true;
    if (this.appName === 'Caser MIMAvet') {
      this.form.value.datePrice = '0';
    }
  }

  addSchedule() {
    this.viewModalNested = true;
  }

  async onSubmit() {
    this.form.markAllAsTouched();
    console.log(this.form.value);
    if (!this.form.valid) {
      return;
    }
    const val = this.form.value;

    const newSpecialist: Partial<Specialist> = {
      available: true,
      curriculum: val.curriculum,
      name: val.name,
      email: val.email,
      phoneNumber: val.phoneNumber.e164Number,
      specialty: val.specialty,
      dateDuration: this.form.value.dateDuration.code,
      datePrice: +val.datePrice,
    };

    if (!this.isDiaryEmpty()) {
      newSpecialist.diary = this.diary;
    }

    if (this.selectedFile) {
      const filePath = `specialist/${this.specialistId}/profile`;
      const task = this.storage.upload(filePath, this.selectedFile);
      await lastValueFrom(
        task.snapshotChanges().pipe(
          last(),
          concatMap(() => this.storage.ref(filePath).getDownloadURL()),
          tap((url) => (this.previewUrl = url)),
          catchError((err) => {
            console.log(err);
            alert('dio error');
            return of(null);
          }),
        ),
      );
      newSpecialist.imageUrl = this.previewUrl as string;
      this.createSpecialist(newSpecialist);
      return;
    }

    this.createSpecialist(newSpecialist);
  }

  onAddSchedule() {
    this.formNested.markAllAsTouched();
    if (!this.formNested.valid) {
      return;
    }
    const val = this.formNested.value;
    const day = val.day.code;
    const start = this.formatTime(val.startAt);
    const end = this.formatTime(val.endAt);

    if (!this.diary[day]) {
      this.diary[day] = [];
    }
    this.diary[day].push({ start, end });
    this.formNested.reset();
    this.isInitialTimeSetStartAt = false;
    this.isInitialTimeSetEndAt = false;
  }

  onRemoveSchedule(day: string, time: { start: string; end: string }) {
    this.diary[day] = this.diary[day].filter(
      (t) => !(t.start === time.start && t.end === time.end),
    );
    if (this.diary[day].length === 0) {
      delete this.diary[day];
    }
  }

  onUpload(event: FileUploadHandlerEvent) {
    this.form.patchValue({ fileImage: event.files[0] });
    this.selectedFile = event.files[0];
    generatePreview(this.selectedFile).subscribe({
      next: (previewUrl) => {
        this.previewUrl = previewUrl;
      },
    });
  }

  setInitialTimeStartAt() {
    if (!this.isInitialTimeSetStartAt) {
      const now = new Date();
      now.setMinutes(0, 0, 0);
      this.formNested.get('startAt').setValue(now);
      this.isInitialTimeSetStartAt = true;
    }
  }

  setInitialTimeEndAt() {
    if (!this.isInitialTimeSetEndAt) {
      const now = new Date();
      now.setMinutes(0, 0, 0);
      this.formNested.get('endAt').setValue(now);
      this.isInitialTimeSetEndAt = true;
    }
  }

  resetForm() {
    this.form.reset();
    this.previewUrl = null;
    this.selectedFile = null;
    this.viewModal = false;
    this.diary = {};
  }

  resetFormNested() {
    this.formNested.reset();
    this.viewModalNested = false;
    this.isInitialTimeSetStartAt = false;
    this.isInitialTimeSetEndAt = false;
  }

  getDiaryDays(diary: { [key: string]: any }): string[] {
    const weekDaysOrder = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday',
    ];
    return Object.keys(diary).sort((a, b) => weekDaysOrder.indexOf(a) - weekDaysOrder.indexOf(b));
  }

  getDayName(dayKey: string): string {
    return WeekDays[dayKey];
  }

  isDiaryEmpty(): boolean {
    return Object.keys(this.diary).length === 0;
  }

  private createSpecialist(newSpecialist: Partial<Specialist>) {
    this.specialistService
      .createSpeciality(newSpecialist, this.specialistId)
      .pipe(
        tap((speciality) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: `Nuevo especialista agregado`,
            life: 3000,
          });
          this.viewModal = false;
          this.specialistId = this.afs.createId();
          newSpecialist.id = speciality.id;

          this.form.reset();
          this.callCloudFunction(newSpecialist);
        }),
        catchError((error) => {
          console.error('Error al crear ', error);
          return of(null);
        }),
      )
      .subscribe();
  }

  private formatTime(date: Date): string {
    const hours = date.getHours().toString().padStart(2, 'onCreateSpecialist0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  private async callCloudFunction(specialist: Partial<Specialist>) {
    // const url = 'http://127.0.0.1:5001/uvema-app-2b330/europe-west1/onCreateSpecialist';
    const url = 'https://europe-west1-uvema-app-2b330.cloudfunctions.net/onCreateSpecialist';
    try {
      specialist.app = localStorage.getItem('appName');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(specialist),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      console.log('Success', data);
    } catch (error) {
      console.error('Error', error);
    }
  }
}
